import { Card, Grid, Icon, Typography, Box, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Generate from "modules/Reports/Generate";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import { ReportType } from "generated/graphql";
import { sentenceCase, capitalCase } from "change-case";
import { Link } from "react-router-dom";

// Report descriptions mapping
const reportDescriptions = {
  [ReportType.TIME_ENTRIES]: "Track and analyze all time entries across your organization",
  [ReportType.ADDITIONAL_COST_DETAILS]:
    "Review additional cost details for better financial planning",
  [ReportType.COMPANY_OR_CLIENT_DETAILS]: "Access detailed company or client information reports",
  [ReportType.CONTACT_DETAILS]: "View comprehensive contact information and activity",
  [ReportType.CREW_DETAILS]: "Review crew assignments, schedules and performance",
  [ReportType.EQUIPMENT_COST_DETAILS]: "Track equipment costs and utilization metrics",
  [ReportType.JOBS_BY_CONTACT]: "Analyze job assignments by individual contacts",
  [ReportType.JOB_COST_BY_PRODUCT_TYPE]: "Break down job costs by product categories",
  [ReportType.JOB_COST_DETAIL]: "Review every cost on one job in a single customer-ready report",
  [ReportType.JOB_DETAILS]: "Access detailed job information and progress reports",
  [ReportType.ORGANIZATION_PROFITABILITY]:
    "Review company-wide proposed versus actual costs and profit by product type",
  [ReportType.PRODUCT_DETAILS]: "Review product information and performance data",
  [ReportType.PROPOSAL_DETAILS]: "Track proposal status and conversion metrics",
  [ReportType.SALES_BY_CONTACT]: "Analyze sales performance by contact",
  [ReportType.SALES_BY_PRODUCT_SUMMARY]: "View aggregated sales data by product type",
  [ReportType.SALES_BY_PROPOSAL_SOURCE]: "Track sales effectiveness by proposal source",
};

const ReportsView = () => {
  // [''] to unlock all
  const filtered = [ReportType.TIME_ENTRIES];

  const links = Object.values(ReportType)
    .map((type) => ({
      label: capitalCase(type),
      value: type,
      description: reportDescriptions[type] || "Generate detailed reports and analytics",
      icon: getIconForReportType(type),
      route: `/organization/report/${type.toLocaleLowerCase()}`,
    }))
    .filter((type) => filtered.some((val) => !type.value.includes(val)));

  const customLinks = [
    {
      label: "Outstanding Work",
      value: "outstanding_work",
      description: "Track won jobs that are still outstanding in the job pipeline",
      icon: "pending_actions",
      route: "/organization/report/outstanding_work",
    },
    {
      label: "Year over Year Revenue",
      value: "year_over_year_revenue",
      description: "Compare won revenue by month for this year versus last year",
      icon: "bar_chart",
      route: "/organization/report/year_over_year_revenue",
    },
    {
      label: "Job Costing Lapsed",
      value: "job_costing_lapsed",
      description: "Find jobs with missing or incomplete costing and open phases directly for updates",
      icon: "receipt_long",
      route: "/organization/report/job_costing_lapsed",
    },
  ];

  const allLinks = [...customLinks, ...links];

  // Helper function to get appropriate icon for each report type
  function getIconForReportType(type: ReportType) {
    switch (type) {
      case ReportType.TIME_ENTRIES:
        return "schedule";
      case ReportType.ADDITIONAL_COST_DETAILS:
        return "attach_money";
      case ReportType.COMPANY_OR_CLIENT_DETAILS:
        return "business";
      case ReportType.CONTACT_DETAILS:
        return "contacts";
      case ReportType.CREW_DETAILS:
        return "groups";
      case ReportType.EQUIPMENT_COST_DETAILS:
        return "construction";
      case ReportType.JOBS_BY_CONTACT:
        return "work";
      case ReportType.JOB_COST_BY_PRODUCT_TYPE:
        return "category";
      case ReportType.JOB_COST_DETAIL:
        return "receipt_long";
      case ReportType.JOB_DETAILS:
        return "engineering";
      case ReportType.ORGANIZATION_PROFITABILITY:
        return "paid";
      case ReportType.PRODUCT_DETAILS:
        return "inventory";
      case ReportType.PROPOSAL_DETAILS:
        return "description";
      case ReportType.SALES_BY_CONTACT:
        return "person";
      case ReportType.SALES_BY_PRODUCT_SUMMARY:
        return "pie_chart";
      case ReportType.SALES_BY_PROPOSAL_SOURCE:
        return "source";
      default:
        return "article";
    }
  }

  return (
    <DashboardPage>
      <MDBox mb={6} component="form">
        <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box>
                  <MDTypography variant="h4" fontWeight="medium">
                    Reports
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Generate comprehensive reports to gain insights
                  </MDTypography>
                </Box>
                <Icon fontSize="large" color="info">
                  analytics
                </Icon>
              </MDBox>
            </Card>
          </Grid>

          <Grid item md={12}>
            <Box sx={{ mt: 1, mb: 3 }}>
              <MDTypography variant="h6" color="text">
                Available Reports
              </MDTypography>
              <Divider sx={{ my: 1.5 }} />
            </Box>

            <Grid container spacing={3}>
              {allLinks.map((type) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={2.4}
                  xl={2.4}
                  key={type.value}
                  sx={{
                    flexBasis: { lg: "20%", xl: "20%" },
                    maxWidth: { lg: "20%", xl: "20%" },
                  }}
                >
                  <Card
                    elevation={1}
                    sx={{
                      p: 3,
                      minHeight: "180px",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      height: "100%",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Link
                      to={type.route}
                      key={type.value}
                      style={{ textDecoration: "none", height: "100%" }}
                    >
                      <Box display="flex" flexDirection="column" height="100%" gap={1}>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: "info.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              mr: 2,
                            }}
                          >
                            <Icon sx={{ color: "#fff" }}>{type.icon}</Icon>
                          </Box>
                          <MDTypography variant="h6" fontWeight="medium" color="dark">
                            {type.label}
                          </MDTypography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <MDTypography variant="body2" color="text">
                            {type.description}
                          </MDTypography>
                        </Box>
                        <Box>
                          <MDButton variant="outlined" color="info" size="small" fullWidth>
                            Get Report
                          </MDButton>
                        </Box>
                      </Box>
                    </Link>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox my={3}>
        <Generate />
      </MDBox>
    </DashboardPage>
  );
};

export default ReportsView;
